import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input/max'
import type { Country } from 'react-phone-number-input/max'

/**
 * Normalizes the E.164-ish value emitted by PhoneInput once it parses to a
 * valid number — a duplicated country calling code (typed or pasted "+1...")
 * collapses via the parser, and a trunk "0" is stripped explicitly because
 * libphonenumber does not strip it for NANP countries.
 *
 * Partial input is returned unchanged so the value is never rewritten
 * mid-keystroke; normalization applies once the number becomes valid.
 */
export function sanitizePhoneInput(value: string, country: Country): string {
	const parsed = parsePhoneNumber(value, country)
	if (parsed?.isValid()) {
		return parsed.number
	}

	// "+10234..." — trunk zero typed/pasted after the calling code. Only strip
	// when the stripped number is valid: in some countries (e.g. Italy) a
	// leading 0 is a significant part of the national number.
	const prefix = `+${getCountryCallingCode(country)}`
	if (value.startsWith(`${prefix}0`)) {
		const stripped = prefix + value.slice(prefix.length).replace(/^0+/, '')
		const reparsed = parsePhoneNumber(stripped, country)
		if (reparsed?.isValid()) {
			return reparsed.number
		}
	}

	return value
}
